import { useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { AccountService } from '../../services/account.service';
import { BankGuaranteeService } from '../../services/bankGuarantee.service';
import { PERMISSIONS } from '../../constants/permissions';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Account } from '../../interfaces/accounting.interface';
import {
  BankGuarantee, GUARANTEE_STATUS_COLORS, GUARANTEE_TYPES, GuaranteeType,
} from '../../interfaces/bank/cheque.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { helpElement } from '../../help/attrs';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { leafAccounts } from '../../utils/postingAccounts';

type Action = { kind: 'create' } | { kind: 'amend' | 'commission' | 'release' | 'invoke'; g: BankGuarantee };

const STATUSES = ['ISSUED', 'AMENDED', 'RELEASED', 'INVOKED', 'EXPIRED'];

/** Help keys of the guarantee dialog, by kind (literals so the help key scan sees them). */
const GUARANTEE_DLG_HELP: Record<Action['kind'], { paper: Record<string, string>; helpKey: string }> = {
  create: { paper: { 'data-help-page': 'bank.guaranteeCreate.page' }, helpKey: 'bank.guaranteeCreate.save' },
  amend: { paper: { 'data-help-page': 'bank.guaranteeAmend.page' }, helpKey: 'bank.guaranteeAmend.save' },
  commission: { paper: { 'data-help-page': 'bank.guaranteeCommission.page' }, helpKey: 'bank.guaranteeCommission.save' },
  release: { paper: { 'data-help-page': 'bank.guaranteeRelease.page' }, helpKey: 'bank.guaranteeRelease.save' },
  invoke: { paper: { 'data-help-page': 'bank.guaranteeInvoke.page' }, helpKey: 'bank.guaranteeInvoke.save' },
};

const BankGuarantees: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('id');
  const [rows, setRows] = useState<BankGuarantee[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [assets, setAssets] = useState<Account[]>([]);
  const [expenses, setExpenses] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [expiring, setExpiring] = useState('');
  const [action, setAction] = useState<Action | null>(null);

  // dialog form state
  const [bankAccountId, setBankAccountId] = useState('');
  const [number, setNumber] = useState('');
  const [type, setType] = useState<GuaranteeType>('PERFORMANCE');
  const [beneficiary, setBeneficiary] = useState('');
  const [amount, setAmount] = useState('');
  const [issueDate, setIssueDate] = useState(toLocalIsoDate());
  const [expiryDate, setExpiryDate] = useState('');
  const [marginAmount, setMarginAmount] = useState('');
  const [marginAccountId, setMarginAccountId] = useState('');
  const [commissionAccountId, setCommissionAccountId] = useState('');
  const [actionDate, setActionDate] = useState(toLocalIsoDate());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const all = await BankGuaranteeService.list(institutionId, status || undefined, expiring ? Number(expiring) : undefined);
      // Deep link (?id=): show just that guarantee.
      setRows(focusId ? all.filter(g => g.id === focusId) : all);
    } catch (e) {
      toast.showApiError(e, 'Failed to load bank guarantees');
    } finally {
      setLoading(false);
    }
  }, [institutionId, focusId, status, expiring]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        setBanks(await BankAccountService.listByInstitution(institutionId));
        const a = await AccountService.getAccountsByType('ASSET');
        const x = await AccountService.getAccountsByType('EXPENSE');
        setAssets(leafAccounts(Array.isArray(a) ? a : (a?.content ?? [])));
        setExpenses(leafAccounts(Array.isArray(x) ? x : (x?.content ?? [])));
      } catch (e) {
        toast.showApiError(e, 'Failed to load reference data');
      }
    })();
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (a: Action) => {
    setAction(a);
    setNotes(''); setActionDate(toLocalIsoDate());
    if (a.kind === 'create') {
      setBankAccountId(''); setNumber(''); setType('PERFORMANCE'); setBeneficiary(''); setAmount('');
      setIssueDate(toLocalIsoDate()); setExpiryDate(''); setMarginAmount(''); setMarginAccountId(''); setCommissionAccountId('');
    } else if (a.kind === 'amend') {
      setAmount(String(a.g.amount)); setExpiryDate(a.g.expiryDate);
    } else if (a.kind === 'commission') {
      setAmount('');
    }
  };

  const hasMargin = Number(marginAmount) > 0;
  const valid = (() => {
    if (!action) return false;
    switch (action.kind) {
      case 'create':
        return !!bankAccountId && number.trim() && beneficiary.trim() && Number(amount) > 0 && !!expiryDate
          && (!hasMargin || !!marginAccountId);
      case 'amend': return Number(amount) > 0 && !!expiryDate;
      case 'commission': return Number(amount) > 0;
      default: return !!actionDate;
    }
  })();

  const save = async () => {
    if (!action) return;
    try {
      setSaving(true);
      if (action.kind === 'create') {
        await BankGuaranteeService.create({
          institutionId, bankAccountId, guaranteeNumber: number.trim(), guaranteeType: type, beneficiary: beneficiary.trim(),
          amount: Number(amount), issueDate, expiryDate, marginAmount: hasMargin ? Number(marginAmount) : undefined,
          marginAccountId: hasMargin ? marginAccountId : undefined, commissionAccountId: commissionAccountId || undefined,
        });
      } else if (action.kind === 'amend') {
        await BankGuaranteeService.amend(action.g.id, { amount: Number(amount), expiryDate, notes: notes || undefined });
      } else if (action.kind === 'commission') {
        await BankGuaranteeService.commission(action.g.id, {
          amount: Number(amount), commissionDate: actionDate, commissionAccountId: commissionAccountId || undefined,
          notes: notes || undefined,
        });
      } else if (action.kind === 'release') {
        await BankGuaranteeService.release(action.g.id, actionDate, notes || undefined);
      } else {
        await BankGuaranteeService.invoke(action.g.id, actionDate, notes || undefined);
      }
      toast.showSuccess('Saved');
      setAction(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save bank guarantee');
    } finally {
      setSaving(false);
    }
  };

  const isLive = (g: BankGuarantee) => g.status === 'ISSUED' || g.status === 'AMENDED';

  return (
    <Box data-help-page="banking.guarantees.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Bank guarantees</Typography>
        {canManage && <Button data-help="banking.guarantees.new" variant="contained" startIcon={<AddIcon />} onClick={() => open({ kind: 'create' })}>New guarantee</Button>}
      </Box>
      {focusId && (
        <Chip sx={{ mb: 2 }} label="Showing one guarantee (from a link). Click to show all"
          onClick={() => setSearchParams({})} onDelete={() => setSearchParams({})} />
      )}
      <Box data-help-section="banking.guarantees.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="banking.guarantees.statusFilter" select label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ flex: '1 1 180px', maxWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
        </TextField>
        <TextField data-help="banking.guarantees.expiringFilter" select label="Expiring soon" value={expiring} onChange={e => setExpiring(e.target.value)} sx={{ flex: '1 1 180px', maxWidth: 240 }}>
          <MenuItem value="">Any time</MenuItem>
          <MenuItem value="30">Within 30 days</MenuItem>
          <MenuItem value="60">Within 60 days</MenuItem>
          <MenuItem value="90">Within 90 days</MenuItem>
        </TextField>
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer data-help-section="banking.guarantees.table" component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="banking.guarantees.col.number">Guarantee no.</TableCell><TableCell data-help="banking.guarantees.col.type">Type</TableCell><TableCell data-help="banking.guarantees.col.beneficiary">Beneficiary</TableCell>
                <TableCell data-help="banking.guarantees.col.amount" align="right">Amount</TableCell><TableCell data-help="banking.guarantees.col.margin" align="right">Margin</TableCell>
                <TableCell data-help="banking.guarantees.col.expiry">Expiry</TableCell><TableCell data-help="banking.guarantees.col.status">Status</TableCell><TableCell data-help="banking.guarantees.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(g => (
                <TableRow key={g.id} hover>
                  <TableCell>{g.guaranteeNumber}</TableCell>
                  <TableCell>{humanizeEnum(g.guaranteeType)}</TableCell>
                  <TableCell>{g.beneficiary}</TableCell>
                  <TableCell align="right">{formatMoney(g.amount)}</TableCell>
                  <TableCell align="right">{g.marginAmount ? formatMoney(g.marginAmount) : '—'}</TableCell>
                  <TableCell>
                    {formatDate(g.expiryDate)}
                    {isLive(g) && g.daysToExpiry <= 30 && (
                      <Chip size="small" color={g.daysToExpiry < 0 ? 'error' : 'warning'} sx={{ ml: 1 }}
                        label={g.daysToExpiry < 0 ? 'Lapsed' : `${g.daysToExpiry}d`} />
                    )}
                  </TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(g.status)} color={GUARANTEE_STATUS_COLORS[g.status]} /></TableCell>
                  <TableCell>
                    {canManage && isLive(g) && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Button data-help="banking.guarantees.action.amend" size="small" onClick={() => open({ kind: 'amend', g })}>Amend</Button>
                        <Button data-help="banking.guarantees.action.commission" size="small" onClick={() => open({ kind: 'commission', g })}>Commission</Button>
                        <Button data-help="banking.guarantees.action.release" size="small" onClick={() => open({ kind: 'release', g })}>Release</Button>
                        <Button data-help="banking.guarantees.action.invoke" size="small" color="error" onClick={() => open({ kind: 'invoke', g })}>Invoked</Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} align="center">No bank guarantees.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!action} onClose={() => setAction(null)} maxWidth="sm" fullWidth PaperProps={action ? GUARANTEE_DLG_HELP[action.kind].paper as any : undefined}>
        <DialogTitle>
          {action?.kind === 'create' ? 'New bank guarantee'
            : `${humanizeEnum(action?.kind ?? '')} guarantee ${action && 'g' in action ? action.g.guaranteeNumber : ''}`}
        </DialogTitle>
        {action && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.guaranteeDialog.section.body">
            {action.kind === 'create' && (
              <>
                <SearchableSelect<BankAccount> data-help="bank.guaranteeCreate.field.bank" label="Issuing bank account" options={banks} value={bankAccountId} required
                  getOptionLabel={b => `${b.bankName} ${b.accountNumber}`} onChange={id => setBankAccountId(id)} />
                <TextField data-help="bank.guaranteeCreate.field.number" label="Guarantee number" value={number} required fullWidth onChange={e => setNumber(e.target.value)} />
                <TextField data-help="bank.guaranteeCreate.field.type" select label="Type" value={type} fullWidth onChange={e => setType(e.target.value as GuaranteeType)}>
                  {GUARANTEE_TYPES.map(t => <MenuItem key={t} value={t}>{humanizeEnum(t)}</MenuItem>)}
                </TextField>
                <TextField data-help="bank.guaranteeCreate.field.beneficiary" label="Beneficiary" value={beneficiary} required fullWidth onChange={e => setBeneficiary(e.target.value)} />
                <TextField data-help="bank.guaranteeCreate.field.amount" label="Guarantee amount" type="number" value={amount} required fullWidth onChange={e => setAmount(e.target.value)} />
                <TextField data-help="bank.guaranteeCreate.field.issueDate" label="Issue date" type="date" value={issueDate} fullWidth InputLabelProps={{ shrink: true }}
                  onChange={e => setIssueDate(e.target.value)} />
                <TextField data-help="bank.guaranteeCreate.field.expiryDate" label="Expiry date" type="date" value={expiryDate} required fullWidth InputLabelProps={{ shrink: true }}
                  onChange={e => setExpiryDate(e.target.value)} />
                <TextField data-help="bank.guaranteeCreate.field.margin" label="Cash margin held by bank (optional)" type="number" value={marginAmount} fullWidth
                  helperText="Posts Dr margin account / Cr the bank account."
                  onChange={e => setMarginAmount(e.target.value)} />
                {hasMargin && (
                  <AccountAutocomplete data-help="bank.guaranteeCreate.field.marginAccount" accounts={assets} value={marginAccountId} label="Margin deposit account" required
                    onChange={id => setMarginAccountId(id)} />
                )}
                <AccountAutocomplete data-help="bank.guaranteeCreate.field.commissionAccount" accounts={expenses} value={commissionAccountId} label="Commission expense account (optional)"
                  onChange={id => setCommissionAccountId(id)} />
              </>
            )}
            {action.kind === 'amend' && (
              <>
                <TextField data-help="bank.guaranteeAmend.field.amount" label="New amount" type="number" value={amount} required fullWidth onChange={e => setAmount(e.target.value)} />
                <TextField data-help="bank.guaranteeAmend.field.expiryDate" label="New expiry date" type="date" value={expiryDate} required fullWidth InputLabelProps={{ shrink: true }}
                  onChange={e => setExpiryDate(e.target.value)} />
              </>
            )}
            {action.kind === 'commission' && (
              <>
                <TextField data-help="bank.guaranteeCommission.field.amount" label="Commission amount" type="number" value={amount} required fullWidth onChange={e => setAmount(e.target.value)} />
                <AccountAutocomplete data-help="bank.guaranteeCommission.field.account" accounts={expenses} value={commissionAccountId} label="Commission expense account"
                  onChange={id => setCommissionAccountId(id)} />
              </>
            )}
            {(action.kind === 'commission' || action.kind === 'release' || action.kind === 'invoke') && (
              <TextField data-help="bank.guaranteeAct.field.date" label="Date" type="date" value={actionDate} fullWidth InputLabelProps={{ shrink: true }}
                onChange={e => setActionDate(e.target.value)} />
            )}
            {action.kind === 'release' && (
              <Typography variant="body2">Returns any cash margin to the bank account (exact reversal of the margin entry).</Typography>
            )}
            {action.kind !== 'create' && (
              <TextField data-help="bank.guaranteeAct.field.notes" label="Notes" value={notes} fullWidth multiline minRows={2} onChange={e => setNotes(e.target.value)} />
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button data-help="bank.guarantee.dlgCancel" onClick={() => setAction(null)}>Cancel</Button>
          <Button {...helpElement(action ? GUARANTEE_DLG_HELP[action.kind].helpKey : undefined)} variant="contained" disabled={saving || !valid} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankGuarantees;
